import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import { Button, Spinner } from "@fluentui/react-components";
import { DismissRegular, PanelBottomRegular } from "@fluentui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import { migrateUserPlanFragmentInventory } from "@minglun/domain";
import type { AppBootstrap, OptimizationStudy, UserPlan, Workbench } from "@minglun/domain";
import type { SolverProgressV2 } from "@minglun/protocol";
import { planIssues } from './plan-readiness';
import { createSolverRequest, mergeSolverResult } from "./solver-state";
import { activeStudy, addStudy, createBlankStudy, deleteStudy, duplicateStudy, initializeStudies, renameStudy, selectStudy, syncActiveStudy } from "./studies";
import type { TransferPreview } from "./data-transfer";
import { type Page, studyEditorPages, userPages, isStudyPage, developerPages, type AppSnapshot, type ConfirmRequest, type MigrationRequest, clone, formatDate, withDerivedWheelTags, type PageProps } from "./workbench-shared";
import { WindowControls, AppMenuBar, IdeToolbar, ActivityRail, StudyExplorer, DeveloperExplorer } from "./ui/workspace-shell";
import { ExitDialog, ConfirmDialog, MigrationDialog } from "./ui/overlays";
import { StudyWizard, StudyIdentityDialog } from "./pages/study-page";




import { StudyWorkspace } from "./pages/study-workspace";
import { ResultsPage } from "./pages/results-page";
import { PageViewport, ViewStateProvider, ViewStateScope, useViewState } from "./ui/session-state";
import { PersonalPage } from "./pages/personal-page";
import { createProgressStore, LiveRunPanel, LiveRunningStatus } from './ui/live-progress';
import { shareUnchanged, identityVersion } from './ui/immutable-state';
import { flushInputDrafts } from './ui/input-drafts';
import { useWorkspaceDraft } from './ui/use-workspace-draft';
import { useBrowserNavigation } from './ui/browser-navigation';
import { RenderBoundary } from './ui/render-boundary';
const RuntimeLogsPage = lazy(() => import('./pages/logs-page').then(module => ({ default: module.RuntimeLogsPage })));
const CharactersPage = lazy(() => import('./pages/catalog-page').then(module => ({ default: module.CharactersPage })));
const WheelsPage = lazy(() => import('./pages/catalog-page').then(module => ({ default: module.WheelsPage })));
const GlobalSettingsPage = lazy(() => import('./pages/settings-page').then(module => ({ default: module.GlobalSettingsPage })));

export function App({ runtimeKey = "desktop" }: { runtimeKey?: string } = {}): JSX.Element {
  const bootstrap = useQuery({
    // The web shell can replace the runtime without reloading the document
    // when another account signs in. Scope bootstrap data to that runtime so
    // a previous account's plans and license features can never be reused.
    queryKey: ["bootstrap", runtimeKey],
    queryFn: () => window.minglun.bootstrap(),
  });
  let content: JSX.Element;
  if (bootstrap.isPending)
    content = (
      <div
        className="app-status app-loading"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span className="app-status-icon">
          <Spinner size="large" />
        </span>
        <strong>正在打开命轮工坊</strong>
        <span>正在载入授权、配置和工作数据</span>
      </div>
    );
  else if (bootstrap.isError || !bootstrap.data)
    content = (
      <div className="app-status app-error" role="alert">
        <span className="app-status-icon">
          <DismissRegular />
        </span>
        <strong>无法打开命轮工坊</strong>
        <span>
          {bootstrap.error instanceof Error
            ? bootstrap.error.message
            : "应用初始化失败"}
        </span>
        <Button appearance="primary" onClick={() => void bootstrap.refetch()}>
          重新尝试
        </Button>
      </div>
    );
  else if (
    !bootstrap.data.license.isValid ||
    !bootstrap.data.license.features?.includes("desktop")
  )
    content = (
      <LicenseGate
        initial={bootstrap.data}
        retry={() => void bootstrap.refetch()}
      />
    );
  else content = <ViewStateProvider key={runtimeKey} accountKey={runtimeKey.replace(/:\d+$/, '')}><Workshop initial={bootstrap.data} accountKey={runtimeKey.replace(/:\d+$/, '')} /></ViewStateProvider>;
  const usesWorkshopTitlebar =
    bootstrap.data?.license.isValid === true &&
    bootstrap.data.license.features?.includes("desktop") === true;
  return (
    <div className="app-frame">
      {!usesWorkshopTitlebar && (
        <div className="standalone-window-bar">
          <span className="appbar-drag" />
          <WindowControls />
        </div>
      )}
      {content}
    </div>
  );
}

export function LicenseGate({
  initial,
  retry,
}: {
  initial: AppBootstrap;
  retry(): void;
}): JSX.Element {
  const message = initial.license.isValid
    ? "当前授权未包含主程序功能。"
    : initial.license.message;
  return (
    <div className="app-status app-error license-gate" role="alert">
      <span className="app-status-icon">
        <DismissRegular />
      </span>
      <strong>{message}</strong>
      <span>将 license.dat 复制到程序安装目录后重新检查。</span>
      <Button appearance="primary" onClick={retry}>
        重新检查
      </Button>
    </div>
  );
}

export function Workshop({ initial,accountKey='desktop' }: { initial: AppBootstrap;accountKey?:string }): JSX.Element {
  const initialPlan = useMemo(() => initializeStudies(migrateUserPlanFragmentInventory(clone(initial.userPlan), initial.workbench)), [initial]);
  const [workbench, setWorkbench] = useState<Workbench>(() =>
    withDerivedWheelTags(clone(initial.workbench)),
  );
  const [userPlan, setUserPlan] = useState<UserPlan>(() =>
    initialPlan,
  );
  const [savedWorkbench, setSavedWorkbench] = useState(workbench);
  const [savedPlan, setSavedPlan] = useState(userPlan);
  const savingRef = useRef(false);
  const [developerMode, setDeveloperMode] = useState(false);
  const [navigation, setNavigation] = useState<{
    entries: Page[];
    index: number;
  }>({ entries: ["study"], index: 0 });
  const [undoStack, setUndoStack] = useState<AppSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<AppSnapshot[]>([]);
  const editGesture = useRef<{ target: EventTarget; recorded: boolean } | undefined>(undefined);
  const commandActions = useRef<{ save(): void; saveClose(): void; run(): void; close(): void; autoSave(): void } | undefined>(undefined);
  const liveWorkbench = useRef(workbench);
  const livePlan = useRef(userPlan);
  liveWorkbench.current = workbench;
  livePlan.current = userPlan;
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest>();
  const [migrationRequest, setMigrationRequest] = useState<MigrationRequest>();
  const [saving, setSaving] = useState(false);
  const [saveFailure, setSaveFailure] = useState<string>();
  const failedSaveSignatureRef = useRef<string | undefined>(undefined);
  const [busyAction, setBusyAction] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [solverProgress, setSolverProgress] = useState<SolverProgressV2 | undefined>();
  const progressStore = useMemo(createProgressStore, []);
  const solvingRef = useRef(false);
  useEffect(() => () => progressStore.dispose(), [progressStore]);
  const [sidebarVisible, setSidebarVisible] = useViewState('shell.sidebarVisible',
    () => !window.matchMedia("(max-width: 760px), (max-width: 1000px) and (max-height: 500px)").matches,
  );
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false);
  useEffect(() => {
    const openRun = () => setBottomPanelOpen(true);
    window.addEventListener('minglun:show-run', openRun);
    return () => window.removeEventListener('minglun:show-run', openRun);
  }, []);
  const [studyWizardOpen, setStudyWizardOpen] = useState(false);
  const [studyWizardSourceId, setStudyWizardSourceId] = useState<string>();
  const [renameStudyTarget, setRenameStudyTarget] = useState<OptimizationStudy>();
  const [requestedHistoryId, setRequestedHistoryId] = useState<string>();
  const page = navigation.entries[navigation.index] ?? "study";
  const livePage = useRef(page); livePage.current = page;
  const [completedRun, setCompletedRun] = useState<{ id: string; name: string }>();
  const [runFailure,setRunFailure] = useState<string>();
  const pageMeta = [...userPages, ...developerPages].find(
    (item) => item.id === page,
  );
  const developerAllowed =
    initial.license.features?.includes("developer") === true;
  const solverAllowed = initial.license.features?.includes("solver") === true;
  const browserNavigation=useBrowserNavigation(accountKey,navigation,setNavigation,[...userPages.map(x=>x.id),'personal',...(developerAllowed?developerPages.map(x=>x.id):[])]);
  const persistenceSignature = `${identityVersion(workbench)}:${identityVersion(userPlan)}`;
  const workbenchDirty = useMemo(() => shareUnchanged(savedWorkbench, workbench) !== savedWorkbench, [workbench, savedWorkbench]);
  const planDirty = useMemo(() => shareUnchanged(savedPlan, userPlan) !== savedPlan, [userPlan, savedPlan]);
  const hasAnyDirty = workbenchDirty || planDirty;
  const autoSaveEnabled = initial.databasePath === "托管云端资料库";
  const draft=useWorkspaceDraft(accountKey,autoSaveEnabled,{workbench,userPlan},{workbench:savedWorkbench,userPlan:savedPlan},hasAnyDirty,persistenceSignature);
  const snapshot = (): AppSnapshot => ({
    workbench: liveWorkbench.current,
    userPlan: livePlan.current,
  });
  const recordChange = (): void => {
    if (editGesture.current?.recorded) return;
    if (editGesture.current) editGesture.current.recorded = true;
    const current = snapshot();
    setUndoStack((stack) => [...stack.slice(-49), current]);
    setRedoStack([]);
  };
  const updateWorkbench = (
    next: Workbench | ((current: Workbench) => Workbench),
  ): void => {
    const resolved = shareUnchanged(liveWorkbench.current, typeof next === "function" ? next(liveWorkbench.current) : next);
    if (resolved === liveWorkbench.current) return;
    recordChange();
    liveWorkbench.current = resolved;
    setWorkbench(resolved);
  };
  const updateUserPlan = useCallback((
    next: UserPlan | ((current: UserPlan) => UserPlan),
  ): void => {
    const resolved = shareUnchanged(livePlan.current, syncActiveStudy(typeof next === "function" ? next(livePlan.current) : next));
    if (resolved === livePlan.current) return;
    recordChange();
    livePlan.current = resolved;
    setUserPlan(resolved);
  }, []);
  const undo = (): void => {
    const previous = undoStack.at(-1);
    if (!previous) return;
    const currentSnapshot = snapshot();
    setRedoStack((stack) => [...stack.slice(-49), currentSnapshot]);
    setUndoStack((stack) => stack.slice(0, -1));
    liveWorkbench.current = previous.workbench; setWorkbench(previous.workbench);
    livePlan.current = previous.userPlan; setUserPlan(previous.userPlan);
  };
  const redo = (): void => {
    const next = redoStack.at(-1);
    if (!next) return;
    const currentSnapshot = snapshot();
    setUndoStack((stack) => [...stack.slice(-49), currentSnapshot]);
    setRedoStack((stack) => stack.slice(0, -1));
    liveWorkbench.current = next.workbench; setWorkbench(next.workbench);
    livePlan.current = next.userPlan; setUserPlan(next.userPlan);
  };
  const navigate = (next: Page): void =>
    setNavigation((current) => {
      if (current.entries[current.index] === next) return current;
      const entries = [...current.entries.slice(0, current.index + 1), next];
      return { entries, index: entries.length - 1 };
    });
  const goBack = browserNavigation.back;
  const goForward = browserNavigation.forward;
  useEffect(
    () =>
      window.minglun.onSolverProgress(progress => { progressStore.set(progress); }),
    [],
  );
  useEffect(() => {
    if (!notice) return undefined;
    const timeout = window.setTimeout(() => setNotice(undefined), 2400);
    return () => window.clearTimeout(timeout);
  }, [notice]);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px), (max-width: 1000px) and (max-height: 500px)");
    const closeExplorerOnMobile = (event: MediaQueryListEvent): void => {
      if (event.matches) setSidebarVisible(false);
    };
    media.addEventListener("change", closeExplorerOnMobile);
    return () => media.removeEventListener("change", closeExplorerOnMobile);
  }, []);
  const saveAll = async ({
    automatic = false,
    signature = persistenceSignature,
  }: {
    automatic?: boolean;
    signature?: string;
  } = {}): Promise<boolean> => {
    if (savingRef.current) return false;
    if(draft.loading || draft.recovery){setNotice('请先处理本机草稿');return false;}
    savingRef.current = true;
    const submittedWorkbench = liveWorkbench.current;
    const submittedPlan = livePlan.current;
    const submittedDeveloperMode = developerMode;
    failedSaveSignatureRef.current = signature;
    setSaveFailure(undefined);
    setSaving(true);
    try {
      const saved = await window.minglun.saveState({
        workbench: submittedWorkbench,
        userPlan: submittedPlan,
        developerMode: submittedDeveloperMode,
      });
      const persistedWorkbench = shareUnchanged(submittedWorkbench, withDerivedWheelTags(saved.workbench));
      const persistedPlan = shareUnchanged(submittedPlan, initializeStudies(saved.userPlan));
      setWorkbench(current => current === submittedWorkbench ? persistedWorkbench : current);
      setUserPlan(current => current === submittedPlan ? persistedPlan : current);
      setSavedWorkbench(persistedWorkbench);
      setSavedPlan(persistedPlan);
      failedSaveSignatureRef.current = undefined;
      if (!automatic) setNotice("缓存与求解记录已保存");
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "保存失败";
      setSaveFailure(message);
      setNotice(automatic ? `自动保存失败：${message}` : message);
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const saveCurrent = async (): Promise<void> => {
    failedSaveSignatureRef.current = undefined;
    await saveAll({ signature: persistenceSignature });
  };
  const requestClose = (): void => {
    if (hasAnyDirty) setClosePromptOpen(true);
    else window.minglun.closeWindow();
  };
  const focusCurrentSearch = (): void => {
    const input = document.querySelector<HTMLInputElement>(
      '.page-motion input[data-search-input="true"]',
    );
    if (!input) {
      setNotice("当前编辑器没有可用的搜索框");
      return;
    }
    input.focus();
    input.select();

  };
  const saveAllAndClose = async (): Promise<void> => {
    failedSaveSignatureRef.current = undefined;
    if (saving || !(await saveAll({ signature: persistenceSignature }))) return;
    window.minglun.closeWindow();
  };
  useEffect(() => {
    if (saveFailure && failedSaveSignatureRef.current !== persistenceSignature)
      setSaveFailure(undefined);
  }, [persistenceSignature, saveFailure]);
  useEffect(() => {
    if (
      !autoSaveEnabled || draft.loading || draft.recovery ||
      !hasAnyDirty ||
      saving ||
      failedSaveSignatureRef.current === persistenceSignature
    ) return undefined;
    const timeout = window.setTimeout(() => {
      void saveAll({ automatic: true, signature: persistenceSignature });
    }, 650);
    return () => window.clearTimeout(timeout);
  }, [autoSaveEnabled, hasAnyDirty, persistenceSignature, saving,draft.loading,draft.recovery]);
  useEffect(()=>{
    const retry=()=>{failedSaveSignatureRef.current=undefined;commandActions.current?.autoSave();};
    window.addEventListener('online',retry);return()=>window.removeEventListener('online',retry);
  },[]);
  useEffect(() => {
    if (!autoSaveEnabled) return undefined;
    const flushPendingSave = (): void => {
      flushInputDrafts();
      commandActions.current?.autoSave();
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") flushPendingSave();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", flushPendingSave);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", flushPendingSave);
    };
  }, [autoSaveEnabled, hasAnyDirty, persistenceSignature, saving]);
  useEffect(() => {
    window.minglun.setWindowDirty(hasAnyDirty);
  }, [hasAnyDirty]);
  useEffect(() => window.minglun.onCloseRequested(requestClose), [hasAnyDirty]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const modifier = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();
      if ((event.altKey && ['arrowleft', 'arrowright'].includes(key)) || (modifier && (/^Digit[1-6]$/.test(event.code) || key === 'n'))) flushInputDrafts();
      // Numeric inputs have their own native undo while focused. Leave text
      // editing and browser/system shortcuts alone; commit before app actions.
      if (modifier && ['z', 'y'].includes(key) && event.target instanceof HTMLElement && event.target.matches('input:not([type="range"]), textarea, [contenteditable="true"]')) return;
      if (modifier && ['s', 'enter', 'w'].includes(key)) {
        flushInputDrafts();
        event.preventDefault();
        if (key === 's') { if (event.shiftKey) commandActions.current?.saveClose(); else commandActions.current?.save(); return; }
        if (key === 'enter') { commandActions.current?.run(); return; }
        if (key === 'w') { commandActions.current?.close(); return; }
      }
      const shiftedDigit = (
        { "!": "1", "@": "2", "#": "3", $: "4", "%": "5", "^": "6" } as Record<
          string,
          string
        >
      )[key];
      const shortcutDigit = /^Digit[1-6]$/.test(event.code)
        ? event.code.slice(-1)
        : /^[1-6]$/.test(key)
          ? key
          : shiftedDigit;
      if (modifier && event.shiftKey && key === "s") {
        event.preventDefault();
        setNotice("正在保存并关闭");
        void saveAllAndClose();
        return;
      }
      if (modifier && key === "s") {
        event.preventDefault();
        void saveCurrent();
        return;
      }
      if (modifier && key === "z") {
        event.preventDefault();
        if (event.shiftKey) {
          if (redoStack.length) {
            redo();
            setNotice("已重做");
          }
        } else if (undoStack.length) {
          undo();
          setNotice("已撤销");
        }
        return;
      }
      if (modifier && key === "y") {
        event.preventDefault();
        if (redoStack.length) {
          redo();
          setNotice("已重做");
        }
        return;
      }
      if (modifier && key === "w") {
        event.preventDefault();
        requestClose();
        return;
      }
      if (modifier && key === "n") {
        event.preventDefault();
        openNewStudy();
        return;
      }
      if (modifier && key === "b") {
        event.preventDefault();
        setSidebarVisible((visible) => !visible);
        return;
      }
      if (modifier && key === "j") {
        event.preventDefault();
        setBottomPanelOpen((open) => !open);
        return;
      }
      if (modifier && key === "enter") {
        event.preventDefault();
        if (solverAllowed && !solverProgress) {
          setNotice("快捷键：开始计算");
          void solve();
        }
        return;
      }
      if (modifier && key === "f") {
        event.preventDefault();
        focusCurrentSearch();
        return;
      }
      if (event.altKey && key === "arrowleft") {
        event.preventDefault();
        if (navigation.index > 0) {
          goBack();

        }
        return;
      }
      if (event.altKey && key === "arrowright") {
        event.preventDefault();
        if (navigation.index < navigation.entries.length - 1) {
          goForward();

        }
        return;
      }
      if (key === "escape" && closePromptOpen) {
        setClosePromptOpen(false);
        return;
      }
      if (key === "escape" && notice) {
        setNotice(undefined);
        return;
      }
      if (modifier && shortcutDigit) {
        const target = event.shiftKey
          ? developerPages[Number(shortcutDigit) - 1]
          : userPages[Number(shortcutDigit) - 1];
        if (
          target &&
          (!event.shiftKey || (developerMode && developerAllowed))
        ) {
          event.preventDefault();
          navigate(target.id);

        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    page,
    workbench,
    userPlan,
    saving,
    notice,
    closePromptOpen,
    developerMode,
    developerAllowed,
    undoStack,
    redoStack,
    navigation,
    solverAllowed,
    solverProgress,
  ]);
  const toggleDeveloper = (checked: boolean): void => {
    setDeveloperMode(checked);
    if (!checked && developerPages.some((item) => item.id === page))
      setNavigation({ entries: ["study"], index: 0 });
  };
  const runBusy = async <T,>(
    key: string,
    action: () => Promise<T>,
  ): Promise<T | undefined> => {
    if (busyAction) return undefined;
    setBusyAction(key);
    try {
      return await action();
    } finally {
      setBusyAction(undefined);
    }
  };
  const confirmAction = (request: ConfirmRequest): void =>
    setConfirmRequest(request);
  const openNewStudy = (sourceId?: string): void => {
    const limit = window.minglunCloudLimits?.studyLimit;
    if (limit !== undefined && (userPlan.studies?.length ?? 0) >= limit) {
      setNotice(`方案数量已达上限 ${limit}，请先删除不再需要的方案`);
      return;
    }
    setStudyWizardSourceId(sourceId);
    setStudyWizardOpen(true);
  };
  const createStudy = (values: { name: string; description: string; sourceId?: string }): void => {
    const limit = window.minglunCloudLimits?.studyLimit;
    if (limit !== undefined && (userPlan.studies?.length ?? 0) >= limit) {
      setStudyWizardOpen(false);
      setNotice(`方案数量已达上限 ${limit}，请先删除不再需要的方案`);
      return;
    }
    const next = values.sourceId
      ? duplicateStudy(userPlan, values.sourceId, values.name, values.description)
      : addStudy(userPlan, createBlankStudy(workbench, userPlan, values.name, values.description));
    updateUserPlan(next);
    setStudyWizardOpen(false);
    navigate("base");
    setNotice(`已创建方案“${values.name}”`);
  };
  const activateStudy = (studyId: string, targetPage: Page = "study"): void => {
    updateUserPlan(plan => selectStudy(plan, studyId));
    navigate(targetPage);
  };
  const requestDeleteStudy = (study: OptimizationStudy): void => {
    if ((userPlan.studies?.length ?? 0) <= 1) {
      setNotice("至少保留一个优化方案");
      return;
    }
    confirmAction({
      title: `删除方案“${study.name}”？`,
      message: "方案配置及其关联的全部结果记录将一并删除，且无法恢复。",
      confirmLabel: "删除方案",
      onConfirm: () => {
        updateUserPlan(deleteStudy(userPlan, study.id));
        navigate("study");
        setNotice("方案及其关联结果已删除");
      },
    });
  };
  const previewMigration = <T,>(kind: "方案" | "结果", preview: TransferPreview<T>, onConfirm: () => void): void => {
    if (!preview.warnings.length && !preview.defaultsAdded && !preview.remapped) { onConfirm(); return; }
    setMigrationRequest({ kind, count: preview.items.length, warnings: preview.warnings, defaultsAdded: preview.defaultsAdded, remapped: preview.remapped, onConfirm });
  };
  const importStudies = async (): Promise<void> => {
    try {
      const preview = await runBusy("import-studies", () =>
        window.minglun.importStudies(workbench),
      );
      if (!preview?.items.length) return;
      const limit = window.minglunCloudLimits?.studyLimit;
      if (limit !== undefined && (userPlan.studies?.length ?? 0) + preview.items.length > limit) { setNotice(`导入后方案数量超过账户上限 ${limit}`); return; }
      previewMigration("方案", preview, () => {
        const next = { ...syncActiveStudy(userPlan), studies: [...(userPlan.studies ?? []), ...preview.items] };
        updateUserPlan(selectStudy(next, preview.items[0]!.id));
        navigate("study");
        setNotice(`已导入 ${preview.items.length} 个方案`);
      });
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "导入方案失败");
    }
  };
  const exportStudies = async (all: boolean): Promise<void> => {
    try {
      const studies = all ? (userPlan.studies ?? []) : activeStudy(userPlan) ? [activeStudy(userPlan)!] : [];
      if (!studies.length) { setNotice("暂无可导出的方案"); return; }
      const path = await runBusy("export-studies", () =>
        window.minglun.exportStudies(studies, workbench),
      );
      if (path) setNotice(all ? `已导出 ${studies.length} 个方案` : "已导出当前方案");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "导出方案失败");
    }
  };
  const importHistory = async (): Promise<void> => {
    try {
      const preview = await runBusy("import-history", () =>
        window.minglun.importResultHistory(workbench, userPlan.studies ?? []),
      );
      if (!preview?.items.length) return;
      const entries = preview.items;
      const importedIds = new Set(entries.map((entry) => entry.id));
      const merged = [
        ...entries,
        ...userPlan.solverHistory.filter((entry) => !importedIds.has(entry.id)),
      ];
      const limit = window.minglunCloudLimits?.resultLimit;
      if (limit !== undefined && merged.length > limit) {
        setNotice(`导入后将有 ${merged.length} 条记录，超过账户上限 ${limit}`);
        return;
      }
      previewMigration("结果", preview, () => updateUserPlan({
        ...userPlan, solverHistory: merged,
      }));
      if (preview.warnings.length || preview.defaultsAdded || preview.remapped) setNotice(`已预检 ${entries.length} 条求解记录，请确认迁移`);
      else setNotice(`已导入 ${entries.length} 条求解记录`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "导入求解记录失败");
    }
  };
  const exportHistory = async (): Promise<void> => {
    try {
      if (!userPlan.solverHistory.length) {
        setNotice("暂无可导出的求解记录");
        return;
      }
      const path = await runBusy("export-history", () =>
        window.minglun.exportResultHistory(userPlan.solverHistory, workbench),
      );
      if (path) setNotice(`已导出 ${userPlan.solverHistory.length} 条求解记录`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "导出求解记录失败");
    }
  };
  const solve = async (): Promise<void> => {
    flushInputDrafts();
    if (solvingRef.current || !solverAllowed) return;
    const runPlan = livePlan.current;
    const runWorkbench = liveWorkbench.current;
    const resultLimit = window.minglunCloudLimits?.resultLimit;
    if (resultLimit !== undefined && runPlan.solverHistory.length >= resultLimit) {
      setNotice(`求解记录已达上限 ${resultLimit}，请先删除不再需要的记录`);
      return;
    }
    const issues = planIssues(runWorkbench, runPlan);
    if (issues[0]) {
      navigate(issues[0].section);
      requestAnimationFrame(()=>window.dispatchEvent(new CustomEvent('minglun:plan-issue',{detail:issues[0]!})));
      return;
    }
    setRunFailure(undefined); setCompletedRun(undefined);
    solvingRef.current = true;
    try {
      const request = createSolverRequest(runWorkbench, runPlan);
      const preparing: SolverProgressV2 = {
        status: "running",
        stage: "preparing",
        stageLabel: "准备求解",
        startedAt: Date.now(),
      };
      progressStore.set(preparing, true);
      setSolverProgress(preparing);
      setBottomPanelOpen(true);
      const result = await window.minglun.solve(request);
      if (result.status === "cancelled" || result.status === "failed") {
        setRunFailure(result.status === "cancelled" ? "计算已取消，尚无可保留结果" : (result.message ?? "计算失败，请重试"));
        return;
      }
      const metadata = {
        id: crypto.randomUUID(),
        title: new Date(result.completedAt).toLocaleString(),
        createdAt: result.completedAt,
        ...(runPlan.activeStudyId ? { studyId: runPlan.activeStudyId } : {}),
        studyName: activeStudy(runPlan)?.name ?? runPlan.name,
      };
      setUserPlan((current) =>
        syncActiveStudy(mergeSolverResult(current, request, result, metadata)),
      );
      const unchanged = livePlan.current.activeStudyId === runPlan.activeStudyId && livePlan.current.inventory === runPlan.inventory && livePlan.current.objectives === runPlan.objectives && livePlan.current.baseAttributes === runPlan.baseAttributes && livePlan.current.solver === runPlan.solver && liveWorkbench.current === runWorkbench;
      if (unchanged && isStudyPage(livePage.current)) {
        setRequestedHistoryId(metadata.id);
        navigate("results");
      } else setCompletedRun({id:metadata.id,name:metadata.studyName});
      setBottomPanelOpen(false);
    } catch (error) {
      setRunFailure(error instanceof Error ? error.message : "计算失败，请重试");
    } finally {
      solvingRef.current = false;
      progressStore.set(undefined, true);
      setSolverProgress(undefined);
    }
  };
  const activePage = renderPage(page, {
    workbench,
    userPlan,
    setWorkbench: updateWorkbench,
    setUserPlan: updateUserPlan,
    solve,
    solverProgress,
    solverAllowed:
      solverAllowed,
    notify: setNotice,
      confirmAction,
      navigate,
      importResults: importHistory,
      exportAllResults: exportHistory,
    ...(requestedHistoryId ? { requestedHistoryId } : {}),
  });
  const currentStudy = activeStudy(userPlan);
  commandActions.current = { save: () => void saveCurrent(), saveClose: () => void saveAllAndClose(), run: () => { if (solverAllowed && !solverProgress) void solve(); }, close: requestClose, autoSave: () => { if (hasAnyDirty && !saving) { failedSaveSignatureRef.current = undefined; void saveAll({ automatic: true, signature: persistenceSignature }); } } };
  const focusedStudyEditor = studyEditorPages.some((item) => item.id === page);
  const developerResourcePage = developerMode && developerAllowed && ["characters", "wheels", "logs"].includes(page);
  const showStudyExplorer = sidebarVisible && isStudyPage(page);
  const showDeveloperExplorer = sidebarVisible && developerResourcePage;
  const showContextExplorer = showStudyExplorer || showDeveloperExplorer;
  const studyReady = userPlan.objectives.some((item) => item.weight > 0) &&
    workbench.optimizationMetrics
      .filter((metric) => metric.requiresBaseValue && metric.id !== "metric.global.element-resilience" && metric.id !== "metric.fate-value")
      .every((metric) => (userPlan.baseAttributes.find((item) => item.metricId === metric.id)?.value ?? 0) > 0);
  const showStudyStatus = isStudyPage(page) || page === "results";
  return (
    <div className={`workshop-frame ${bottomPanelOpen ? "bottom-panel-visible" : ""}`}
      onPointerDownCapture={(event) => { if ((event.target as HTMLElement).closest('button, [role="button"], [role="menuitem"]')) flushInputDrafts(); }}
      onFocusCapture={(event) => { if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) editGesture.current = event.target instanceof HTMLInputElement && event.target.type === 'range' ? undefined : { target: event.target, recorded: false }; }}
      onBlurCapture={(event) => { const target = event.target; queueMicrotask(() => { if (editGesture.current?.target === target) editGesture.current = undefined; }); }}
    >
      <IdeToolbar
        saveState={saveFailure ? <button className="status-save-state status-save-error" title={`${saveFailure}${draft.error?`；${draft.error}`:''}`} onClick={()=>void saveCurrent()}>{draft.locallySaved?'本机已保存 · 同步重试':'同步失败 · 重试'}</button> : <span className="status-save-state" role="status" title={draft.error??(window.minglunCloudLimits?.localPreview?'隔离示例数据，仅保存在此浏览器，不连接正式账号':undefined)}>{draft.recovery?'发现本机草稿':saving ? '正在同步…' : hasAnyDirty ? (draft.locallySaved?'本机已保存 · 待同步':draft.error?'草稿未保存 · 请及时同步':autoSaveEnabled ? '正在保存草稿…' : '未保存') : window.minglunCloudLimits?.localPreview?'预览 · 本机示例':autoSaveEnabled ? '已同步云端' : '已保存'}</span>}
        onToggleSidebar={()=>setSidebarVisible(visible=>!visible)}
        onImport={()=>void importStudies()} onExport={()=>void exportStudies(false)}
        onRename={()=>currentStudy && setRenameStudyTarget(currentStudy)} onDuplicate={()=>openNewStudy(currentStudy?.id)} onClose={requestClose}
        page={page}
        studyName={currentStudy?.name ?? userPlan.name}
        pageLabel={pageMeta?.label ?? "工作区"}
        canGoBack={navigation.index > 0}
        canGoForward={navigation.index < navigation.entries.length - 1}
        onBack={goBack}
        onForward={goForward}
        canUndo={undoStack.length > 0}
        canRedo={redoStack.length > 0}
        onUndo={undo}
        onRedo={redo}
        saving={saving}
        dirty={hasAnyDirty}
        onSave={() => void saveCurrent()}
        onNewStudy={() => openNewStudy()}
        onOpenStudy={() => { navigate("study"); setSidebarVisible(true); }}
        onOpenDeveloper={() => { toggleDeveloper(true); navigate("characters"); setSidebarVisible(true); }}
        onRun={() => void solve()}
        running={Boolean(solverProgress)}
        solverAllowed={solverAllowed}
        onShowRun={() => setBottomPanelOpen(true)}
      />
      {draft.recovery && <div className="draft-recovery" role="region" aria-label="恢复本机草稿"><div><strong>发现未同步的本机草稿</strong><p>{new Date(draft.recovery.draft.savedAt).toLocaleString('zh-CN')} · {draft.recovery.catalogChanged?'目录版本已变化，请先导出草稿再通过导入兼容。':draft.recovery.cloudChanged?'云端内容与草稿起点不同。恢复前请确认要使用的版本。':'上次编辑尚未同步，可以恢复继续。'}</p></div>
        <Button appearance="secondary" onClick={()=>{const value=draft.recovery!.draft.value;void window.minglun.exportStudies(value.userPlan.studies??[],value.workbench);}}>导出草稿方案</Button>
        <Button onClick={()=>setConfirmRequest({title:'放弃本机草稿？',message:'保留当前云端内容，删除此账户的本机未同步草稿。',confirmLabel:'保留云端',onConfirm:()=>{void draft.discard().catch(()=>setNotice('草稿删除失败，请重试'));}})}>保留云端</Button>
        <Button appearance="primary" disabled={draft.recovery.catalogChanged} onClick={()=>setConfirmRequest({title:'恢复本机草稿？',message:draft.recovery!.cloudChanged?'云端内容已有变化。恢复后本机草稿将成为当前工作内容，随后尝试同步；新发生的云端冲突会阻止同步。':'恢复上次未同步的方案和结果，并继续自动保存。',confirmLabel:'恢复草稿',onConfirm:()=>{const value=draft.recovery!.draft.value;recordChange();setWorkbench(value.workbench);setUserPlan(value.userPlan);draft.accept();}})}>恢复草稿</Button>
      </div>}
      <div className={`ide-body unified-body ${sidebarVisible ? "sidebar-expanded" : "sidebar-collapsed"}`}>
        <aside className="unified-sidebar"><div className="sidebar-brand">命轮工坊</div>
        <ActivityRail
          page={page}
          developerMode={developerMode && developerAllowed}
          developerAllowed={developerAllowed}
          onStudies={() => {
            navigate("study"); setSidebarVisible(true);
          }}
          onResults={() => { navigate("results"); setSidebarVisible(true); }}
          onDeveloper={() => {
            if (developerResourcePage) setSidebarVisible((visible) => !visible);
            else { toggleDeveloper(true); navigate("characters"); setSidebarVisible(true); }
          }}
          onSettings={() => { if (developerAllowed) { toggleDeveloper(true); navigate("global-settings"); } }}
          onPersonal={() => navigate("personal")}
        />
        {showContextExplorer && (
          <button
            type="button"
            className="mobile-explorer-backdrop"
            aria-label="关闭侧边栏"
            onClick={() => setSidebarVisible(false)}
          />
        )}
        {showStudyExplorer && (
          <StudyExplorer
            studies={userPlan.studies ?? []}
            activeStudyId={userPlan.activeStudyId}
            histories={userPlan.solverHistory}
            onNew={() => openNewStudy()}
            onSelectStudy={(studyId) => {
              activateStudy(studyId);
              if (window.matchMedia("(max-width: 760px), (max-width: 1000px) and (max-height: 500px)").matches)
                setSidebarVisible(false);
            }}
            onDuplicate={(studyId) => openNewStudy(studyId)}
            onRename={(study) => setRenameStudyTarget(study)}
            onDelete={requestDeleteStudy}
            onImport={() => void importStudies()}
            onExportCurrent={() => void exportStudies(false)}
            onExportAll={() => void exportStudies(true)}
          />
        )}
        {showDeveloperExplorer && <DeveloperExplorer page={page} onOpenPage={(target) => {
          navigate(target);
          if (window.matchMedia("(max-width: 760px), (max-width: 1000px) and (max-height: 500px)").matches)
            setSidebarVisible(false);
        }} />}
        <div id="result-sidebar-slot" hidden={page !== 'results' || !sidebarVisible} />
        {sidebarVisible && page === 'personal' && <div className="sidebar-caption">账户、设置与资料</div>}
        </aside>
        <main className="workspace ide-workspace">
          {(completedRun || runFailure) && <div className="run-outcome" role="status">{completedRun ? <><span>“{completedRun.name}”计算完成</span><button onClick={()=>{setRequestedHistoryId(completedRun.id);navigate('results');setCompletedRun(undefined);}}>查看结果</button></> : <span>{runFailure}</span>}<button aria-label="关闭运行提示" onClick={()=>{setCompletedRun(undefined);setRunFailure(undefined);}}><DismissRegular/></button></div>}
          <section className={`page-host ${focusedStudyEditor ? "study-focus-body" : ""}`}>
            <ViewStateScope scope={isStudyPage(page) ? `study:${userPlan.activeStudyId}` : page}>
              <PageViewport key={`${isStudyPage(page) ? "study" : page}:${isStudyPage(page) ? userPlan.activeStudyId : ''}`} name={`${isStudyPage(page) ? "study" : page}:${isStudyPage(page) ? userPlan.activeStudyId : ''}`}>
                <RenderBoundary scope="page" resetKey={`${page}:${userPlan.activeStudyId}`}>
                <Suspense fallback={<div className="page-loading" role="status"><Spinner size="small" />正在打开…</div>}>
                  {page === 'personal' ? <PersonalPage customer={initial.license.customer ?? '我的账户'} developerAllowed={developerAllowed} onNavigate={(target) => { if (developerAllowed) { toggleDeveloper(true); navigate(target); } }} /> : activePage}
                </Suspense>
                </RenderBoundary>
              </PageViewport>
            </ViewStateScope>
          </section>
        </main>
        {notice && (
          <div
            className="app-notice"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice(undefined)}
              aria-label="关闭提示"
            >
              <DismissRegular />
            </button>
          </div>
        )}
      </div>
      {bottomPanelOpen && (
        <LiveRunPanel
          store={progressStore}
          studyName={currentStudy?.name ?? userPlan.name}
          onCancel={() => void window.minglun.cancelSolve()}
          onClose={() => setBottomPanelOpen(false)}
        />
      )}
      {closePromptOpen && (
        <ExitDialog
          saving={saving}
          onSave={() => void saveAllAndClose()}
          onDiscard={() => window.minglun.closeWindow()}
          onCancel={() => setClosePromptOpen(false)}
        />
      )}
      {confirmRequest && (
        <ConfirmDialog
          request={confirmRequest}
          onClose={() => setConfirmRequest(undefined)}
        />
      )}
      {migrationRequest && <MigrationDialog request={migrationRequest} onClose={() => setMigrationRequest(undefined)} />}
      {studyWizardOpen && (
        <StudyWizard
          studies={userPlan.studies ?? []}
          initialSourceId={studyWizardSourceId}
          onCancel={() => setStudyWizardOpen(false)}
          onConfirm={createStudy}
        />
      )}
      {renameStudyTarget && (
        <StudyIdentityDialog
          study={renameStudyTarget}
          existingNames={(userPlan.studies ?? []).filter((study) => study.id !== renameStudyTarget.id).map((study) => study.name)}
          onCancel={() => setRenameStudyTarget(undefined)}
          onConfirm={(name, description) => {
            updateUserPlan(renameStudy(userPlan, renameStudyTarget.id, name, description));
            setRenameStudyTarget(undefined);
            setNotice("方案信息已更新");
          }}
        />
      )}
    </div>
  );
}

export function renderPage(page: Page, props: PageProps): JSX.Element {
  switch (page) {
    case "personal":
      return <></>;
    case "study":
    case "objectives":
    case "base":
    case "resources":
    case "solver":
      return <StudyWorkspace {...props} section={page} />;
    case "results":
      return <ResultsPage {...props} />;
    case "characters":
      return <CharactersPage {...props} />;
    case "wheels":
      return <WheelsPage {...props} />;
    case "global-settings":
      return <GlobalSettingsPage {...props} />;
    case "logs":
      return <RuntimeLogsPage {...props} />;
  }
}
